/**
 * Układ modułów PV: na połaci skośnej (z bryla.js), na dachu płaskim (rzędy na południe
 * albo wschód-zachód) i na konstrukcji gruntowej.
 *
 * Na połaci pracujemy w jej układzie (u wzdłuż okapu, v w górę spadku, metry od `poczatek`).
 * Obszar roboczy to wielokąt połaci pomniejszony o marginesy krawędzi (przecięcie
 * półpłaszczyzn, połacie są wypukłe). Rzędy idą od okapu; w pasie rzędu bierzemy część
 * wspólną szerokości wielokąta na dole i na górze pasa. Dla wielokąta wypukłego to
 * dokładny przedział, w którym moduł mieści się na całej wysokości.
 * Przeszkody wycinają tylko sloty nachodzące na ich prostokąt z zapasem: siatka nie
 * przesuwa się po dodaniu komina, a usunięte moduły wracają w to samo miejsce.
 *
 * Moduł: u, v, n to osie płaszczyzny modułu, szer wzdłuż u, wys wzdłuż v. Próbki cienia
 * na wierzchu modułu: probki[3 * polowa + pas], pasy (substringi diod bocznikujących)
 * biegną wzdłuż dłuższego boku, połówki (half-cut) dzielą dłuższy bok.
 */

import {
  dodaj, odejmij, skaluj, skalarny, wektorowy, uvPolaci, azymutNormalnej,
} from './bryla'

const RAD = Math.PI / 180
const EPS = 1e-9
const Y = [0, 1, 0]
const PRZERWA_WSCHOD_ZACHOD = 0.3

export const MODUL_DOMYSLNY = { dlugosc: 1.722, szerokosc: 1.134, grubosc: 0.03 }
export const MARGINES_DOMYSLNY = { okap: 0.3, kalenica: 0.3, boki: 0.3 }

/* ═══ Geometria 2D ══════════════════════════════════════════════════════════ */

const pole2D = (w) => {
  let s = 0
  for (let i = 0; i < w.length; i++) {
    const a = w[i]
    const b = w[(i + 1) % w.length]
    s += a[0] * b[1] - b[0] * a[1]
  }
  return s / 2
}

// Zostawia część wielokąta, w której nx·u + ny·v ≥ c (Sutherland–Hodgman).
const przytnij = (w, nx, ny, c) => {
  const wynik = []
  for (let i = 0; i < w.length; i++) {
    const a = w[i]
    const b = w[(i + 1) % w.length]
    const da = nx * a[0] + ny * a[1] - c
    const db = nx * b[0] + ny * b[1] - c
    if (da >= 0) wynik.push(a)
    if ((da >= 0) !== (db >= 0)) {
      const t = da / (da - db)
      wynik.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
    }
  }
  return wynik.length >= 3 ? wynik : []
}

// Przedział [lo, hi] przekroju wielokąta prostą v = const albo null.
const zakresPoziomy = (w, v) => {
  let lo = Infinity
  let hi = -Infinity
  for (let i = 0; i < w.length; i++) {
    const a = w[i]
    const b = w[(i + 1) % w.length]
    if ((a[1] - v) * (b[1] - v) > 0) continue
    if (Math.abs(b[1] - a[1]) < 1e-12) {
      lo = Math.min(lo, a[0], b[0])
      hi = Math.max(hi, a[0], b[0])
      continue
    }
    const u = a[0] + (b[0] - a[0]) * (v - a[1]) / (b[1] - a[1])
    lo = Math.min(lo, u)
    hi = Math.max(hi, u)
  }
  return lo <= hi ? [lo, hi] : null
}

const kategoriaKrawedzi = (rodzaj) => (rodzaj === 'okap' || rodzaj === 'kalenica' ? rodzaj : 'boki')

// Gdy połać nie podaje rodzajów krawędzi: pozioma na dole to okap, pozioma wyżej kalenica.
const rodzajeZGeometrii = (w2) => {
  const vMin = Math.min(...w2.map((p) => p[1]))
  return w2.map((a, i) => {
    const b = w2[(i + 1) % w2.length]
    if (Math.abs(b[1] - a[1]) > 1e-6 * Math.max(1, Math.abs(b[0] - a[0]))) return 'boki'
    return Math.abs(a[1] - vMin) < 1e-6 ? 'okap' : 'kalenica'
  })
}

/**
 * Obszar roboczy połaci: wielokąt [[u, v]...] (przeciwnie do wskazówek zegara) po odsunięciu
 * każdej krawędzi do środka o margines jej rodzaju (narożniki i krawędzie szczytowe to „boki”).
 * Pusta tablica, gdy marginesy zjadają całą połać.
 */
export const obszarRoboczy = (polac, margines = {}) => {
  const m = { ...MARGINES_DOMYSLNY, ...margines }
  let w2 = polac.wierzcholki.map((p) => uvPolaci(polac, p))
  let rodzaje = polac.rodzajeKrawedzi || rodzajeZGeometrii(w2)
  if (pole2D(w2) < 0) {
    const n = w2.length
    w2 = [...w2].reverse()
    rodzaje = w2.map((_, j) => rodzaje[(2 * n - 2 - j) % n])
  }
  let K = w2
  for (let i = 0; i < w2.length; i++) {
    const a = w2[i]
    const b = w2[(i + 1) % w2.length]
    const dl = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (dl < EPS) continue
    const nx = -(b[1] - a[1]) / dl
    const ny = (b[0] - a[0]) / dl
    K = przytnij(K, nx, ny, nx * a[0] + ny * a[1] + (m[kategoriaKrawedzi(rodzaje[i])] || 0))
    if (!K.length) return []
  }
  return pole2D(K) > 1e-9 ? K : []
}

// Prostokąt [u0, v0, u1, v1] rzutu przeszkody powiększony o zapas.
const prostokatPrzeszkody = (przeszkoda, rzut) => {
  const p2 = (przeszkoda.punkty || []).map(rzut)
  const z = przeszkoda.zapas ?? 0.3
  if (!p2.length) return null
  return [
    Math.min(...p2.map((p) => p[0])) - z, Math.min(...p2.map((p) => p[1])) - z,
    Math.max(...p2.map((p) => p[0])) + z, Math.max(...p2.map((p) => p[1])) + z,
  ]
}

const nachodzi = (a, b) => a[0] < b[2] - EPS && b[0] < a[2] - EPS && a[1] < b[3] - EPS && b[1] < a[3] - EPS

// Slot [u0, v0, u1, v1] i wielokąt wypukły mają wspólne wnętrze (oś rozdzielająca); sam styk się nie liczy.
const nachodziWielokat = (slot, w) => {
  const T = 1e-7
  const naroza = [[slot[0], slot[1]], [slot[2], slot[1]], [slot[2], slot[3]], [slot[0], slot[3]]]
  const osie = [[1, 0], [0, 1], ...w.map((a, i) => {
    const b = w[(i + 1) % w.length]
    return [b[1] - a[1], a[0] - b[0]]
  })]
  return osie.every(([nx, ny]) => {
    const dl = Math.hypot(nx, ny)
    if (dl < EPS) return true
    const pw = w.map((p) => (p[0] * nx + p[1] * ny) / dl)
    const ps = naroza.map((p) => (p[0] * nx + p[1] * ny) / dl)
    return Math.max(...pw) > Math.min(...ps) + T && Math.max(...ps) > Math.min(...pw) + T
  })
}

/**
 * Test slotu dla przeszkody: `strefaNaDachu` (wielokąt wypukły z zapasem, punkty świata na dachu, z
 * `przeszkody.js`) albo dawne `punkty` z `zapas` (prostokąt opisany na ich rzucie). `rzut`: punkt → [a, b].
 */
const kolizjaPrzeszkody = (przeszkoda, rzut) => {
  if (przeszkoda.strefaNaDachu?.length >= 3) {
    const w = przeszkoda.strefaNaDachu.map(rzut)
    return (slot) => nachodziWielokat(slot, w)
  }
  const p = prostokatPrzeszkody(przeszkoda, rzut)
  return p ? (slot) => nachodzi(slot, p) : null
}

const zbiorUsunietych = (usuniete) => (usuniete instanceof Set ? usuniete : new Set(usuniete || []))

/* ═══ Moduł ═════════════════════════════════════════════════════════════════ */

const zbudujModul = ({ id, polacId, rzad, kol, srodek, u, v, n, szer, wys, grubosc, ...reszta }) => {
  const wierzch = dodaj(srodek, skaluj(n, grubosc / 2))
  const dlugiWzdluzV = wys >= szer
  const probki = []
  for (let polowa = 0; polowa < 2; polowa++) {
    for (let pas = 0; pas < 3; pas++) {
      const wPasie = (pas - 1) / 3
      const wPolowie = polowa === 0 ? -0.25 : 0.25
      const du = dlugiWzdluzV ? wPasie * szer : wPolowie * szer
      const dv = dlugiWzdluzV ? wPolowie * wys : wPasie * wys
      probki.push(dodaj(wierzch, dodaj(skaluj(u, du), skaluj(v, dv))))
    }
  }
  return { id, polacId, rzad, kol, ...reszta, srodek, u, v, n, szer, wys, grubosc, probki }
}

// Osie modułu nachylonego o kąt w stronę poziomego kierunku f: n na zewnątrz, v w górę spadku.
const osieNachylone = (f, kat) => {
  const c = Math.cos(kat * RAD)
  const s = Math.sin(kat * RAD)
  const n = dodaj(skaluj(f, s), skaluj(Y, c))
  const v = dodaj(skaluj(f, -c), skaluj(Y, s))
  return { u: wektorowy(v, n), v, n }
}

const wymiaryModulu = (modul, orientacja) => ({
  szer: orientacja === 'poziom' ? modul.dlugosc : modul.szerokosc,
  wys: orientacja === 'poziom' ? modul.szerokosc : modul.dlugosc,
  grubosc: modul.grubosc ?? MODUL_DOMYSLNY.grubosc,
})

/* ═══ Połać skośna ══════════════════════════════════════════════════════════ */

// Rzędy dla danego przesunięcia bloku nad dolną krawędzią obszaru: { r, va, lo, hi, k }.
const rzedyObszaru = (K, { szer, wys, odstep, przesuniecie, liczbaRzedow, vMin, vMax }) => {
  const rzedy = []
  for (let r = 0; r < liczbaRzedow; r++) {
    const va = vMin + przesuniecie + r * (wys + odstep)
    const z1 = zakresPoziomy(K, Math.max(va, vMin))
    const z2 = zakresPoziomy(K, Math.min(va + wys, vMax))
    if (!z1 || !z2) continue
    const lo = Math.max(z1[0], z2[0])
    const hi = Math.min(z1[1], z2[1])
    const k = Math.floor((hi - lo + odstep + 1e-9) / (szer + odstep))
    if (k > 0) rzedy.push({ r, va, lo, hi, k })
  }
  return rzedy
}

/**
 * Wypełnia połać modułami. Zwraca { moduly, wolne, szyny }: `wolne` to sloty z `usuniete`
 * (do pokazania jako „przywróć”), szyny po dwie na rząd pod ciągłymi odcinkami modułów,
 * 5 cm nad pokryciem. Przy wyrównaniu 'srodek' każdy rząd jest wyśrodkowany, a blok
 * rzędów przesuwany w pionie w 9 wariantach: wygrywa największa liczba modułów, przy
 * remisie wariant najbliższy środka. 'okap' kładzie pierwszy rząd na dolnym marginesie.
 * `przesuniecieV` (metry, plus w górę dachu; strzałki w Projekcie 3D, 2026-10-30) przesuwa siatkę od tego położenia: rząd 0
 * idzie o tyle w górę, kolejne nad nim, a rzędy poniżej rzędu 0 mają numery od `liczbaRzedow` wzwyż (od najbliższego), więc
 * zapełniają się na końcu. Rzędy poza obszarem roboczym znikają; czy pozwolić na ruch, rozstrzyga `ruchModulowMozliwy`.
 */
export const ukladNaPolaci = ({
  polac,
  modul = MODUL_DOMYSLNY,
  orientacja = 'pion',
  odstep = 0.02,
  margines = MARGINES_DOMYSLNY,
  przeszkody = [],
  wyrownanie = 'srodek',
  wysuniecie = 0.1,
  usuniete = new Set(),
  przesuniecieV = 0,
}) => {
  const moduly = []
  const wolne = []
  const szyny = []
  const K = obszarRoboczy(polac, margines)
  if (!K.length) return { moduly, wolne, szyny }
  const { szer, wys, grubosc } = wymiaryModulu(modul, orientacja)
  const vMin = Math.min(...K.map((p) => p[1]))
  const vMax = Math.max(...K.map((p) => p[1]))
  const liczbaRzedow = Math.floor((vMax - vMin + odstep + 1e-9) / (wys + odstep))
  if (liczbaRzedow < 1) return { moduly, wolne, szyny }
  const luz = Math.max(0, vMax - vMin - (liczbaRzedow * (wys + odstep) - odstep))
  const warianty = wyrownanie === 'okap' ? [0] : [4, 3, 5, 2, 6, 1, 7, 0, 8].map((j) => luz * j / 8)
  let najlepsze = null
  warianty.forEach((przesuniecie) => {
    const rzedy = rzedyObszaru(K, { szer, wys, odstep, przesuniecie, liczbaRzedow, vMin, vMax })
    const suma = rzedy.reduce((s, r) => s + r.k, 0)
    if (!najlepsze || suma > najlepsze.suma) najlepsze = { rzedy, suma, przesuniecie }
  })
  if (przesuniecieV) {
    const naObszarze = ({ va }) => va >= vMin - 1e-9 && va + wys <= vMax + 1e-9
    const faza = najlepsze.przesuniecie + przesuniecieV
    const skok = wys + odstep
    const wyzej = rzedyObszaru(K, { szer, wys, odstep, przesuniecie: faza, liczbaRzedow, vMin, vMax }).filter(naObszarze)
    const nizej = rzedyObszaru(K, { szer, wys, odstep, przesuniecie: faza - liczbaRzedow * skok, liczbaRzedow, vMin, vMax })
      .filter(naObszarze)
      .map((x) => ({ ...x, r: liczbaRzedow + (liczbaRzedow - 1 - x.r) }))
    najlepsze = { rzedy: [...wyzej, ...nizej] }
  }

  const { u, v, normalna: n, poczatek } = polac
  const naSwiat = (uu, vv, h) => dodaj(poczatek, dodaj(dodaj(skaluj(u, uu), skaluj(v, vv)), skaluj(n, h)))
  const kolizje = przeszkody.map((p) => kolizjaPrzeszkody(p, (pt) => uvPolaci(polac, pt))).filter(Boolean)
  const usun = zbiorUsunietych(usuniete)

  najlepsze.rzedy.forEach(({ r, va, lo, hi, k }) => {
    const u0 = lo + (hi - lo - (k * szer + (k - 1) * odstep)) / 2
    const vc = va + wys / 2
    const odcinki = []
    for (let kol = 0; kol < k; kol++) {
      const uc = u0 + kol * (szer + odstep) + szer / 2
      const slot = [uc - szer / 2, va, uc + szer / 2, va + wys]
      if (kolizje.some((jest) => jest(slot))) continue
      const m = zbudujModul({
        id: `${polac.id}:${r}:${kol}`, polacId: polac.id, rzad: r, kol,
        srodek: naSwiat(uc, vc, wysuniecie + grubosc / 2), u, v, n, szer, wys, grubosc,
      })
      if (usun.has(m.id)) {
        wolne.push(m)
        continue
      }
      moduly.push(m)
      const ost = odcinki[odcinki.length - 1]
      if (ost && ost.kol === kol - 1) Object.assign(ost, { kol, u1: slot[2] })
      else odcinki.push({ kol, u0: slot[0], u1: slot[2] })
    }
    odcinki.forEach(({ u0: a, u1: b }) => {
      ;[vc - wys / 4, vc + wys / 4].forEach((vs) => {
        szyny.push({ polacId: polac.id, rzad: r, od: naSwiat(a - 0.05, vs, 0.05), do: naSwiat(b + 0.05, vs, 0.05) })
      })
    })
  })
  return { moduly, wolne, szyny }
}

/* ═══ Dach płaski ═══════════════════════════════════════════════════════════ */

// Wysokość słońca w prawdziwe południe 21 grudnia, stopnie.
export const wysokoscSloncaZima = (szerokoscGeograficzna) => 90 - szerokoscGeograficzna - 23.44

// Kąt między poziomym kierunkiem a południem, stopnie 0–180.
const odPoludnia = (f) => Math.abs((((azymutNormalnej(f) - 180) % 360) + 540) % 360 - 180)

/**
 * Moduły na dachu płaskim w prostokącie `obszar` (jak skrzydło: srodek [x, z], dlugosc
 * wzdłuż x', szerokosc wzdłuż z', obrot). 'poludnie': rzędy patrzą w stronę boku
 * prostokąta najbliższego południa, rozstaw bez cienia w południe 21 XII (przerwa =
 * przewyższenie · cos(odchylenie od południa) / tg(wysokości słońca)); `odstepRzedow`
 * nadpisuje tę przerwę (wolna odległość między rzędami w poziomie). 'wschod_zachod':
 * pary plecami do siebie, kalenice wzdłuż osi prostokąta bliższej N–S, 0,3 m między parami.
 * Zwraca { moduly, wolne, wsporniki, rozstawRzedow }.
 */
export const ukladNaDachuPlaskim = ({
  obszar,
  y = 0,
  modul = MODUL_DOMYSLNY,
  orientacja = 'poziom',
  kat = 13,
  uklad = 'poludnie',
  szerokoscGeograficzna = 52,
  margines = 0.6,
  odstepRzedow,
  odstep = 0.02,
  przeswit = 0.15,
  przeszkody = [],
  usuniete = new Set(),
}) => {
  const moduly = []
  const wolne = []
  const wsporniki = []
  const th = (obszar.obrot || 0) * RAD
  const ex = [Math.cos(th), 0, -Math.sin(th)]
  const ez = [Math.sin(th), 0, Math.cos(th)]
  const C = [obszar.srodek[0], y, obszar.srodek[1]]
  const { szer, wys, grubosc } = wymiaryModulu(modul, orientacja)
  const glebokosc = wys * Math.cos(kat * RAD)
  const przewyzszenie = wys * Math.sin(kat * RAD)
  const usun = zbiorUsunietych(usuniete)
  const a0 = (k) => -(k * szer + (k - 1) * odstep) / 2
  const liczbaWzdluz = (L) => Math.max(0, Math.floor((L + odstep + 1e-9) / (szer + odstep)))

  // Wspólne dla obu układów: moduł o środku podstawy w C + wa·a + wb·b, patrzący w f.
  const dodajModul = ({ id, rzad, kol, strona, wa, wb, a, b, f, kolizje }) => {
    const slot = [a - szer / 2, b - glebokosc / 2, a + szer / 2, b + glebokosc / 2]
    if (kolizje.some((jest) => jest(slot))) return
    const { u, v, n } = osieNachylone(f, kat)
    const podstawa = dodaj(C, dodaj(skaluj(wa, a), skaluj(wb, b)))
    const srodek = dodaj(podstawa, dodaj([0, przeswit + przewyzszenie / 2, 0], skaluj(n, grubosc / 2)))
    const m = zbudujModul({
      id, polacId: 'plaski', rzad, kol, ...(strona ? { strona } : {}), srodek, u, v, n, szer, wys, grubosc,
    })
    if (usun.has(id)) {
      wolne.push(m)
      return
    }
    moduly.push(m)
    wsporniki.push({
      modulId: id, pozycja: podstawa, kierunek: f, wzdluz: u, szerokosc: szer, glebokosc,
      wysokoscNiska: przeswit, wysokoscWysoka: przeswit + przewyzszenie,
    })
  }
  const rzutuj = (wa, wb) => (P) => {
    const d = odejmij(P, C)
    return [skalarny(d, wa), skalarny(d, wb)]
  }

  if (uklad === 'wschod_zachod') {
    const grzbietWzdluzZ = Math.abs(Math.cos(th)) >= Math.abs(Math.sin(th))
    const wr = grzbietWzdluzZ ? ez : ex
    let wp = grzbietWzdluzZ ? ex : ez
    if (wp[0] < -EPS || (Math.abs(wp[0]) <= EPS && wp[2] < 0)) wp = skaluj(wp, -1)
    const L = (grzbietWzdluzZ ? obszar.szerokosc : obszar.dlugosc) - 2 * margines
    const W = (grzbietWzdluzZ ? obszar.dlugosc : obszar.szerokosc) - 2 * margines
    const para = 2 * glebokosc
    const nP = W >= para - 1e-9 ? Math.floor((W - para) / (para + PRZERWA_WSCHOD_ZACHOD) + 1e-9) + 1 : 0
    const k = liczbaWzdluz(L)
    const blok = nP * para + (nP - 1) * PRZERWA_WSCHOD_ZACHOD
    const kolizje = przeszkody.map((p) => kolizjaPrzeszkody(p, rzutuj(wr, wp))).filter(Boolean)
    for (let j = 0; j < nP; j++) {
      const grzbiet = -blok / 2 + j * (para + PRZERWA_WSCHOD_ZACHOD) + glebokosc
      ;[['w', 1], ['z', -1]].forEach(([strona, znak]) => {
        for (let kol = 0; kol < k; kol++) {
          dodajModul({
            id: `plaski:${j}:${kol}:${strona}`, rzad: j, kol, strona, wa: wr, wb: wp,
            a: a0(k) + kol * (szer + odstep) + szer / 2, b: grzbiet + znak * glebokosc / 2,
            f: skaluj(wp, znak), kolizje,
          })
        }
      })
    }
    return { moduly, wolne, wsporniki, rozstawRzedow: para + PRZERWA_WSCHOD_ZACHOD }
  }

  // 'poludnie': bok prostokąta najbliższy południa (przy remisie kolejność listy).
  const boki = [
    { f: ez, glebokosc: obszar.szerokosc, dlugosc: obszar.dlugosc },
    { f: ex, glebokosc: obszar.dlugosc, dlugosc: obszar.szerokosc },
    { f: skaluj(ex, -1), glebokosc: obszar.dlugosc, dlugosc: obszar.szerokosc },
    { f: skaluj(ez, -1), glebokosc: obszar.szerokosc, dlugosc: obszar.dlugosc },
  ]
  const bok = boki.reduce((best, b) => (odPoludnia(b.f) < odPoludnia(best.f) - 1e-9 ? b : best))
  const f = bok.f
  const wa = wektorowy(Y, f)
  const slonce = Math.max(1, wysokoscSloncaZima(szerokoscGeograficzna)) * RAD
  const przerwa = odstepRzedow ?? przewyzszenie * Math.cos(odPoludnia(f) * RAD) / Math.tan(slonce)
  const rozstaw = glebokosc + przerwa
  const D = bok.glebokosc - 2 * margines
  const nR = D >= glebokosc - 1e-9 ? Math.floor((D - glebokosc) / rozstaw + 1e-9) + 1 : 0
  const k = liczbaWzdluz(bok.dlugosc - 2 * margines)
  const glebBloku = (nR - 1) * rozstaw + glebokosc
  const kolizje = przeszkody.map((p) => kolizjaPrzeszkody(p, rzutuj(wa, f))).filter(Boolean)
  for (let r = 0; r < nR; r++) {
    for (let kol = 0; kol < k; kol++) {
      dodajModul({
        id: `plaski:${r}:${kol}`, rzad: r, kol, wa, wb: f,
        a: a0(k) + kol * (szer + odstep) + szer / 2, b: glebBloku / 2 - r * rozstaw - glebokosc / 2,
        f, kolizje,
      })
    }
  }
  return { moduly, wolne, wsporniki, rozstawRzedow: rozstaw }
}

/* ═══ Konstrukcja gruntowa ══════════════════════════════════════════════════ */

/**
 * Stół gruntowy rzedy × kolumny, front (dolna krawędź) w stronę +z' obrotu, czyli
 * azymut 180 − obrot. `srodek` to środek rzutu stołu na teren, dolna krawędź modułów
 * na wysokości `wysokoscDolna`. Nogi parami (przód, tył) co najwyżej co 2,5 m,
 * belki: płatwie wzdłuż stołu i krokwie wzdłuż spadku.
 */
export const ukladGruntowy = ({
  srodek = [0, 0],
  obrot = 0,
  rzedy = 2,
  kolumny = 10,
  kat = 30,
  wysokoscDolna = 0.8,
  modul = MODUL_DOMYSLNY,
  orientacja = 'poziom',
  odstep = 0.02,
  y = 0,
  usuniete = new Set(),
}) => {
  const th = obrot * RAD
  const f = [Math.sin(th), 0, Math.cos(th)]
  const { u, v, n } = osieNachylone(f, kat)
  const { szer, wys, grubosc } = wymiaryModulu(modul, orientacja)
  const szerStolu = kolumny * szer + (kolumny - 1) * odstep
  const dlStolu = rzedy * wys + (rzedy - 1) * odstep
  const glebokosc = dlStolu * Math.cos(kat * RAD)
  const dolnaKrawedz = [srodek[0] + f[0] * glebokosc / 2, y + wysokoscDolna, srodek[1] + f[2] * glebokosc / 2]
  const naStole = (a, s, h = 0) => dodaj(dolnaKrawedz, dodaj(dodaj(skaluj(u, a), skaluj(v, s)), skaluj(n, h)))
  const usun = zbiorUsunietych(usuniete)
  const moduly = []
  const wolne = []
  for (let r = 0; r < rzedy; r++) {
    for (let kol = 0; kol < kolumny; kol++) {
      const m = zbudujModul({
        id: `grunt:${r}:${kol}`, polacId: 'grunt', rzad: r, kol,
        srodek: naStole(-szerStolu / 2 + kol * (szer + odstep) + szer / 2, r * (wys + odstep) + wys / 2, grubosc / 2),
        u, v, n, szer, wys, grubosc,
      })
      if (usun.has(m.id)) wolne.push(m)
      else moduly.push(m)
    }
  }
  const liczbaNog = Math.max(2, Math.ceil(szerStolu / 2.5) + 1)
  const skrajna = Math.max(0, szerStolu / 2 - 0.3)
  const pozycjeNog = Array.from({ length: liczbaNog }, (_, i) => -skrajna + (2 * skrajna * i) / (liczbaNog - 1))
  const linieNog = [0.15 * dlStolu, 0.85 * dlStolu]
  const nogi = []
  pozycjeNog.forEach((a) => linieNog.forEach((s) => {
    const gora = naStole(a, s, -0.08)
    nogi.push({ podstawa: [gora[0], y, gora[2]], wysokosc: gora[1] - y })
  }))
  const belki = [
    ...linieNog.map((s) => ({ od: naStole(-szerStolu / 2, s, -0.04), do: naStole(szerStolu / 2, s, -0.04) })),
    ...pozycjeNog.map((a) => ({ od: naStole(a, 0, -0.08), do: naStole(a, dlStolu, -0.08) })),
  ]
  return { moduly, wolne, nogi, belki }
}

/* ═══ Konstrukcja gruntowa w obszarze (wielokąt) ════════════════════════════ */

// Przejście serwisowe między parami stołów wschód-zachód na gruncie (na dachu płaskim 0,3 m).
const PRZERWA_GRUNT_WSCHOD_ZACHOD = 1.0
// Górna granica miejsc w obszarze: większe pole i tak nie jest instalacją przydomową, a analiza cienia
// liczy każdy moduł osobno.
export const MAKS_MIEJSC_W_OBSZARZE = 1500

/** Pole wielokąta [[x, z]...] w m² (bez znaku). */
export const poleObszaru = (w) => (w && w.length >= 3 ? Math.abs(pole2D(w)) : 0)

// Odcinki ab i cd przecinają się we wnętrzu (wspólny koniec sąsiednich boków się nie liczy).
const przecinaja = (a, b, c, d) => {
  const o = (p, q, r) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0])
  const d1 = o(c, d, a)
  const d2 = o(c, d, b)
  const d3 = o(a, b, c)
  const d4 = o(a, b, d)
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
}

/** Wielokąt bez przecinających się boków (obszar zaznaczany na mapie klikaniem naroży). */
export const wielokatProsty = (w) => {
  if (!w || w.length < 3) return false
  const n = w.length
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue
      if (przecinaja(w[i], w[(i + 1) % n], w[j], w[(j + 1) % n])) return false
    }
  }
  return true
}

// Przedziały [lo, hi] przekroju wielokąta (także wklęsłego) prostą b = const, rosnąco.
const przekroj = (w, b) => {
  const xs = []
  for (let i = 0; i < w.length; i++) {
    const a = w[i]
    const c = w[(i + 1) % w.length]
    if ((a[1] > b) === (c[1] > b)) continue
    xs.push(a[0] + (c[0] - a[0]) * (b - a[1]) / (c[1] - a[1]))
  }
  xs.sort((x, y) => x - y)
  const wynik = []
  for (let i = 0; i + 1 < xs.length; i += 2) if (xs[i + 1] > xs[i]) wynik.push([xs[i], xs[i + 1]])
  return wynik
}

const czescWspolna = (A, B) => {
  const wynik = []
  let i = 0
  let j = 0
  while (i < A.length && j < B.length) {
    const lo = Math.max(A[i][0], B[j][0])
    const hi = Math.min(A[i][1], B[j][1])
    if (hi > lo) wynik.push([lo, hi])
    if (A[i][1] < B[j][1]) i++
    else j++
  }
  return wynik
}

/**
 * Przedziały [lo, hi] (wzdłuż pierwszej współrzędnej), w których cały pas b0 ≤ b ≤ b1 leży
 * w wielokącie [[a, b]...]. Krawędzie zmieniają się liniowo między wierzchołkami, więc wystarczą
 * przekroje na brzegach pasa i na wysokości każdego wierzchołka w jego środku.
 */
export const przedzialyPasa = (w, b0, b1) => {
  const bs = w.map((p) => p[1])
  const bMin = Math.min(...bs) + 1e-7
  const bMax = Math.max(...bs) - 1e-7
  const lo = Math.max(b0, bMin)
  const hi = Math.min(b1, bMax)
  if (hi < lo || b0 < bMin - 1e-6 || b1 > bMax + 1e-6) return []
  const proby = [lo, hi, ...bs.filter((b) => b > lo && b < hi)]
  let wynik = null
  for (const b of proby) {
    const p = przekroj(w, b)
    wynik = wynik ? czescWspolna(wynik, p) : p
    if (!wynik.length) break
  }
  return wynik || []
}

// Lista rozłącznych przedziałów bez przedziału [lo, hi].
const odejmijPrzedzial = (lista, [lo, hi]) => lista.flatMap(([a, b]) => {
  if (hi <= a || lo >= b) return [[a, b]]
  return [...(lo > a ? [[a, lo]] : []), ...(hi < b ? [[hi, b]] : [])]
})

/**
 * Stoły gruntowe ułożone rzędami w obszarze `obszar` ([[x, z]...] w metrach sceny, dowolny wielokąt
 * prosty). 'poludnie': rzędy biegną w poprzek kierunku `obrot` (0 = front na południe), rozstaw bez cienia
 * w południe 21 XII jak na dachu płaskim; 'wschod_zachod': pary stołów plecami do siebie, kalenice wzdłuż
 * osi północ-południe (obróconej o `obrot`), 1 m przejścia między parami. Każdy rząd to jeden stół na
 * każdy fragment obszaru, w którym mieści się choć jedna kolumna; `margines` od granicy obszaru.
 * Pierwszy rząd od frontu (południa), przesunięcie rzędów wybrane tak, by miejsc było najwięcej.
 * `wykluczenia`: wielokąty [[x, z]...] (rzuty budynków obok), w których, z tym samym marginesem, stołów nie ma.
 *
 * @returns { stoly: [{ id, rzad, kolOd, kolumny, czesci: [{ strona, srodek: [x, z], obrot }] }], rozstaw, miejsca }
 */
export const stolyWObszarze = ({
  obszar,
  obrot = 0,
  uklad = 'poludnie',
  rzedy = 2,
  kat = 30,
  modul = MODUL_DOMYSLNY,
  orientacja = 'poziom',
  odstep = 0.02,
  margines = 0.5,
  szerokoscGeograficzna = 52,
  odstepRzedow,
  maksMiejsc = MAKS_MIEJSC_W_OBSZARZE,
  wykluczenia = [],
}) => {
  const pusto = { stoly: [], rozstaw: 0, miejsca: 0 }
  if (!Array.isArray(obszar) || obszar.length < 3 || poleObszaru(obszar) < 1) return pusto
  const wz = uklad === 'wschod_zachod'
  const th = obrot * RAD
  const f = [Math.sin(th), 0, Math.cos(th)]
  // d: kierunek frontu stołu (przy wschód-zachód front strony wschodniej), a: wzdłuż rzędu.
  const d = wz ? [Math.cos(th), 0, -Math.sin(th)] : f
  const a = [d[2], 0, -d[0]]
  const w2 = obszar.map(([x, z]) => [x * a[0] + z * a[2], x * d[0] + z * d[2]])
  const { szer, wys } = wymiaryModulu(modul, orientacja)
  const dlStolu = rzedy * wys + (rzedy - 1) * odstep
  const glebokosc = dlStolu * Math.cos(kat * RAD)
  const przewyzszenie = dlStolu * Math.sin(kat * RAD)
  const D = wz ? 2 * glebokosc : glebokosc
  const slonce = Math.max(1, wysokoscSloncaZima(szerokoscGeograficzna)) * RAD
  const przerwa = odstepRzedow ?? (wz ? PRZERWA_GRUNT_WSCHOD_ZACHOD : przewyzszenie * Math.cos(odPoludnia(f) * RAD) / Math.tan(slonce))
  const P = D + przerwa
  const bs = w2.map((p) => p[1])
  const bMin = Math.min(...bs)
  const bMax = Math.max(...bs)
  const zakres = bMax - bMin - 2 * margines
  if (zakres < D) return pusto
  const nR = Math.floor((zakres - D) / P + 1e-9) + 1
  const luz = Math.max(0, zakres - D - (nR - 1) * P)
  const krokKol = szer + odstep
  const aMin = Math.min(...w2.map((p) => p[0]))
  const naStronie = wz ? 2 : 1
  const doSceny = (aa, bb) => [aa * a[0] + bb * d[0], aa * a[2] + bb * d[2]]
  const wyk2 = (wykluczenia || []).filter((w) => w && w.length >= 3).map((w) => w.map(([x, z]) => [x * a[0] + z * a[2], x * d[0] + z * d[2]]))
  // Zasięg wykluczenia wzdłuż rzędu w pasie b0..b1 (część wielokąta w pasie), z marginesem; null poza pasem.
  const zasiegWPasie = (w, b0, b1) => {
    const c = przytnij(przytnij(w, 0, 1, b0), 0, -1, -b1)
    if (!c.length) return null
    const as = c.map((q) => q[0])
    return [Math.min(...as) - margines, Math.max(...as) + margines]
  }

  const uloz = (przesuniecie) => {
    const stoly = []
    let miejsca = 0
    for (let j = 0; j < nR && miejsca < maksMiejsc; j++) {
      const b1 = bMax - margines - przesuniecie - j * P
      const b0 = b1 - D
      const przedzialy = przedzialyPasa(w2, b0 - margines, b1 + margines)
      let wolne = przedzialy.map(([lo0, hi0]) => [lo0 + margines, hi0 - margines]).filter(([lo, hi]) => hi > lo)
      for (const w of wyk2) {
        const z = zasiegWPasie(w, b0 - margines, b1 + margines)
        if (z) wolne = odejmijPrzedzial(wolne, z)
      }
      let t = 0
      for (const [lo, hi] of wolne) {
        const k = Math.min(Math.floor((hi - lo + odstep + 1e-9) / krokKol), Math.ceil((maksMiejsc - miejsca) / (rzedy * naStronie)))
        if (k < 1) continue
        const ac = (lo + hi) / 2
        const kolOd = Math.round((ac - (k * krokKol - odstep) / 2 - aMin) / krokKol)
        const bc = (b0 + b1) / 2
        const czesci = wz
          ? [
            { strona: 'w', srodek: doSceny(ac, bc + glebokosc / 2), obrot: obrot + 90 },
            { strona: 'z', srodek: doSceny(ac, bc - glebokosc / 2), obrot: obrot - 90 },
          ]
          : [{ strona: null, srodek: doSceny(ac, bc), obrot }]
        stoly.push({ id: `${j}-${t}`, rzad: j, kolOd, kolumny: k, czesci })
        miejsca += k * rzedy * naStronie
        t++
      }
    }
    return { stoly, miejsca }
  }
  let najlepsze = null
  for (const u of [0.5, 0.25, 0.75, 0, 1]) {
    const w = uloz(luz * u)
    if (!najlepsze || w.miejsca > najlepsze.miejsca) najlepsze = w
  }
  return { ...najlepsze, rozstaw: P }
}

/**
 * Moduły, nogi i belki stołów z `stolyWObszarze`. Identyfikatory miejsc: `${polacId}:${stół}${strona}:${rząd}:${kolumna}`,
 * np. „grunt:0-1w:1:4”; `rzad` to rząd stołów od frontu, a `kol` kolumna w całym rzędzie (kolejność
 * wypełniania w analizie: od frontu, od środka rzędu). Konstrukcja tylko pod zajętymi kolumnami.
 */
export const ukladGruntowyWObszarze = ({
  polacId = 'grunt',
  wysokoscDolna = 0.8,
  y = 0,
  usuniete = new Set(),
  ...ustawienia
}) => {
  const { stoly } = stolyWObszarze(ustawienia)
  const { rzedy = 2, kat = 30, modul = MODUL_DOMYSLNY, orientacja = 'poziom', odstep = 0.02 } = ustawienia
  const { szer } = wymiaryModulu(modul, orientacja)
  const usun = zbiorUsunietych(usuniete)
  const wynik = { moduly: [], wolne: [], nogi: [], belki: [] }
  for (const stol of stoly) {
    for (const czesc of stol.czesci) {
      const prefiks = `${polacId}:${stol.id}${czesc.strona || ''}`
      const parametry = { obrot: czesc.obrot, rzedy, kat, wysokoscDolna, modul, orientacja, odstep, y }
      const c = ukladGruntowy({ ...parametry, srodek: czesc.srodek, kolumny: stol.kolumny })
      // Strona zachodnia patrzy odwrotnie, więc jej kolumny liczymy od drugiego końca (ta sama kolumna rzędu).
      const kolRzedu = (kol) => stol.kolOd + (czesc.strona === 'z' ? stol.kolumny - 1 - kol : kol)
      const zajete = []
      for (const m of [...c.moduly, ...c.wolne]) {
        const x = {
          ...m, id: `${prefiks}:${m.rzad}:${m.kol}`, polacId, rzad: stol.rzad, kol: kolRzedu(m.kol), rzadStolu: m.rzad, pochylony: true,
        }
        if (usun.has(x.id)) wynik.wolne.push(x)
        else { wynik.moduly.push(x); zajete.push(m.kol) }
      }
      if (!zajete.length) continue
      const k0 = Math.min(...zajete)
      const k1 = Math.max(...zajete)
      if (k0 === 0 && k1 === stol.kolumny - 1) {
        wynik.nogi.push(...c.nogi)
        wynik.belki.push(...c.belki)
        continue
      }
      // Częściowo zajęty stół: konstrukcja tylko od pierwszej do ostatniej zajętej kolumny.
      const t = czesc.obrot * RAD
      const u = [Math.cos(t), -Math.sin(t)]
      const przes = ((k0 + k1) / 2 - (stol.kolumny - 1) / 2) * (szer + odstep)
      const sub = ukladGruntowy({ ...parametry, srodek: [czesc.srodek[0] + u[0] * przes, czesc.srodek[1] + u[1] * przes], kolumny: k1 - k0 + 1 })
      wynik.nogi.push(...sub.nogi)
      wynik.belki.push(...sub.belki)
    }
  }
  return wynik
}

/* ═══ Podsumowanie ══════════════════════════════════════════════════════════ */

export const podsumowanie = (moduly = [], mocModuluW = 0) => ({
  liczba: moduly.length,
  mocKwp: Math.round(moduly.length * mocModuluW) / 1000,
  powierzchnia: Math.round(moduly.reduce((s, m) => s + m.szer * m.wys, 0) * 100) / 100,
})

// Pomocnicze do testów i sceny: narożniki modułu na jego wierzchu.
export const narozaModulu = (m) => {
  const w = dodaj(m.srodek, skaluj(m.n, m.grubosc / 2))
  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([a, b]) => (
    dodaj(w, dodaj(skaluj(m.u, a * m.szer / 2), skaluj(m.v, b * m.wys / 2)))
  ))
}
